/**
 * QR-1b: ОТДЕЛЬНЫЙ axios-клиент публичных ручек инвайта (preview/join) —
 * без интерсепторов axiosInstance ЛК (High-1 дизайн-ревью тикета QR-1):
 * у лендинга `/j/` нет сессии владельца, а общий интерсептор на 401 идёт
 * в refresh и при мёртвой cookie делает `window.location.href='/'` —
 * приглашённый с протухшим токеном владельца в том же браузере улетал бы
 * с лендинга на форму логина. Этот клиент Authorization НЕ ставит никогда:
 * токен инвайта едет ТОЛЬКО в JSON-теле (§3.3), в URL его нет.
 *
 * Host — тот же источник, что у axiosInstance (INF-2: пусто = same-origin).
 */
import axios from 'axios';
import { BASE_API_URL } from '../utils/router/axiosInstance';

export const publicAxios = axios.create({
  baseURL: `${BASE_API_URL}/api/v1/`,
  // Cookie владельца (refresh_token) публичным ручкам не нужна и не должна
  // ходить с лендинга; same-origin axios шлёт cookie всегда — это не
  // отключить, но и сервер её на preview/join не читает. Явно без auth.
  withCredentials: false,
});

export class PublicApiError extends Error {
  constructor(status, detail, path = 'invites/preview') {
    super(`HTTP ${status} ${path}`);
    this.status = status;
    this.detail = detail;
    this.path = path;
  }
}

/**
 * QR-3: вход участника по приглашению ПРЯМО В БРАУЗЕРЕ.
 * POST /msg/invites/join -> {member_id, access_token, conversation_id, peer_*}.
 *
 * 🔴 `join_key` — UUID ПОПЫТКИ, а не запроса: сетевой ретрай обязан слать
 * ТОТ ЖЕ ключ, иначе в окне реплея сервер заведёт ВТОРОГО участника на то же
 * приглашение и съест место в потолке. Поэтому ключ рождается у вызывающего
 * (на первую попытку) и передаётся сюда, а не генерируется тут.
 *
 * 🔴 `device_id` тоже выбирает вызывающий: сервер кладёт его клеймом в
 * member-JWT, и он обязан совпасть с did, под которым дальше живёт
 * криптостор браузера.
 */
export async function joinInvite({
  token, displayName, deviceId, joinKey, privacyVersion, resumeToken = null,
}) {
  try {
    const { data } = await publicAxios.post('msg/invites/join', {
      token,
      display_name: displayName,
      device_id: deviceId,
      join_key: joinKey,
      privacy_version: privacyVersion,
      // WEB-19 §4.1 (Д-2): доказательство прежней сессии этого устройства —
      // только с ним сервер продолжает прежнего участника (did не секрет).
      ...(resumeToken ? { resume_token: resumeToken } : {}),
    });
    return data;
  } catch (e) {
    const status = e?.response?.status ?? 0;
    throw new PublicApiError(status, e?.response?.data?.detail ?? null, 'invites/join');
  }
}

/**
 * POST /msg/invites/preview {token} -> {label, install_url, privacy_version,
 * privacy_policy_url}. 404 (неизвестный/отозванный/кривой — неразличимо)
 * -> PublicApiError(404); сеть/5xx -> PublicApiError(status|0).
 */
export async function previewInvite(token) {
  try {
    const { data } = await publicAxios.post('msg/invites/preview', { token });
    return data;
  } catch (e) {
    const status = e?.response?.status ?? 0;
    throw new PublicApiError(status, e?.response?.data?.detail ?? null);
  }
}

/**
 * Д-3: продление сеанса по member-JWT ИЗ ФАЙЛА переноса.
 * POST /msg/members/me/token -> {access_token}. 401 — участника больше нет
 * (удалён владельцем) или токен протух: файл старше года.
 *
 * 🔴 Исключение из правила модуля «Authorization не ставим никогда» —
 * осознанное и единственное. Токен здесь берётся не из сессии браузера, а из
 * файла, который человек только что открыл; проверить его иначе нечем. Ходит
 * запрос ЭТИМ клиентом, а не axiosInstance ЛК, ровно по той же причине, что и
 * preview: общий интерсептор на 401 уводит в refresh и дальше на форму
 * логина — а здесь 401 ОЖИДАЕМ и обязан показать человеку честный экран
 * «доступ закрыт или просрочен», а не выбросить его со страницы.
 *
 * 🔴 Вызывается ДО единой записи в браузер (дизайн §4). Если участника уже
 * нет, в localStorage и IndexedDB не должно лечь ничего: иначе человек
 * получил бы полупризрачный чат, который не открывается и не удаляется.
 */
export async function refreshMemberToken(memberToken) {
  try {
    const { data } = await publicAxios.post('msg/members/me/token', null, {
      headers: { Authorization: `Bearer ${memberToken}` },
    });
    return data.access_token;
  } catch (e) {
    const status = e?.response?.status ?? 0;
    throw new PublicApiError(status, e?.response?.data?.detail ?? null, 'members/me/token');
  }
}
